"use client"

import { getSfxVolume } from "@/lib/audio-settings"

// Sound effect categories and their files
const TOWER_SOUNDS = {
  archer: {
    fire: ["/sounds/towers/archertower-fire1.mp3", "/sounds/towers/archertower-fire2.mp3"],
    hit: ["/sounds/towers/archertower-hit1.mp3", "/sounds/towers/archertower-hit2.mp3"],
  },
  siege: {
    fire: ["/sounds/towers/siegetower-fire.mp3"],
    hit: ["/sounds/towers/siegetower-hit.mp3"],
  },
  mage: {
    fire: ["/sounds/towers/magetower-fire.mp3"],
    hit: ["/sounds/towers/magetower-hit.mp3"],
  },
} as const

// Event sounds
const EVENT_SOUNDS = {
  levelComplete: "/sounds/events/level-victory.mp3",
  defeat: "/sounds/events/defeat.mp3",
  newWave: "/sounds/events/newwave.mp3",
  menuHover: "/sounds/events/menu-hover.mp3",
  singleCoin: "/sounds/events/single-coin.mp3",
  abduction: "/sounds/events/abduction.mp3",
  spaceshipEngine: "/sounds/events/spaceship-engine.mp3",
  shopOpen: "/sounds/events/shop-open.mp3",
  scoreBlip: "/sounds/events/score-blip.mp3",
  levelUp: "/sounds/events/levelup.mp3",
  cloneAwaken: "/sounds/events/clone-awaken.mp3",
  upgradeTreeUpgrade: "/sounds/events/upgradetree-upgrade.mp3",
  skillLevelUp: "/sounds/events/skill-levelup.mp3",
  sell: "/sounds/events/sell.mp3",
  buy: "/sounds/events/buy.mp3",
  enemyFinishLine: "/sounds/events/enemy-finishline.mp3",
  armorEquip: "/sounds/events/armor-equip.mp3",
  armorDrop: "/sounds/events/armor-drop.mp3",
  billboard: "/sounds/events/billboard.mp3",
  itemDrop: "/sounds/events/item_drop.mp3",
} as const

// Tower action sounds
const TOWER_ACTION_SOUNDS = {
  build: "/sounds/towers/tower-build.mp3",
  sell: "/sounds/towers/tower-sell.mp3",
  buildingHit: [
    "/sounds/towers/building-hit1.mp3",
    "/sounds/towers/building-hit2.mp3",
    "/sounds/towers/building-hit3.mp3",
  ],
} as const

// Hero ability sounds
const HERO_SOUNDS = {
  siegesmith: {
    forgeTurret: "/sounds/heroes/siegesmith-buildturret.mp3",
    turretAttack: "/sounds/heroes/siegesmith_turret_attack.mp3",
    tremorSlam: "/sounds/heroes/siegesmith-tremor-slam.mp3",
  },
  paladin: {
    vanguardRushHit: "/sounds/heroes/paladin-vanguard-rush-hit.mp3",
    vanguardRushTravel: "/sounds/heroes/paladin-vanguard-rush-travel.mp3",
    spectralBlade: "/sounds/heroes/paladin-spectral-blade.mp3",
  },
  ranger: {
    entangledRoots: "/sounds/heroes/ranger-entangledroots.mp3",
    wildkinCall: "/sounds/heroes/ranger-wildkin-call.mp3",
    attackAim: "/sounds/heroes/ranger-attackaim.mp3",
    attackRelease: "/sounds/heroes/ranger-attackrelease.mp3",
  },
} as const

// Item sounds
const ITEM_SOUNDS = {
  potion: "/sounds/items/inventory_potion.mp3",
} as const

// Droppod sounds
const DROPPOD_SOUNDS = {
  fly: "/sounds/droppod/droppod-fly.mp3",
  land: "/sounds/droppod/droppod-land.mp3",
  menu: "/sounds/droppod/droppod-menu.mp3",
  menuClick: "/sounds/droppod/droppod-menu-click.mp3",
} as const

type TowerType = keyof typeof TOWER_SOUNDS

// ===== PLAYBACK CORE =====
// Sound effects are fired from the game loop, so a wave of towers can ask for
// dozens of plays per second. HTMLAudioElement.play() is expensive on the main
// thread (especially iOS Safari, where restarting a busy element forces a seek
// and a fresh decode), and that showed up as frame hitching whenever towers
// started shooting. So playback goes through WebAudio: each clip is decoded
// once into an AudioBuffer, after which starting a voice is nearly free.
// HTMLAudioElement remains the fallback until a clip's buffer is ready, and for
// browsers without WebAudio.

// Audio cache to reuse Audio elements (fallback path)
const audioCache: Map<string, HTMLAudioElement[]> = new Map()
const POOL_SIZE = 3 // Number of Audio elements per sound for overlapping plays

// Decoded WebAudio buffers, keyed by src
const bufferCache = new Map<string, AudioBuffer>()
const decodingSources = new Set<string>()
const undecodableSources = new Set<string>()

// Same clip restarting faster than this is inaudible as anything but noise
const MIN_REPLAY_INTERVAL_MS = 45
// Ceiling on simultaneous effect voices, so a big wave can't swamp the mixer
const MAX_CONCURRENT_VOICES = 12

const lastPlayedAt = new Map<string, number>()
let activeVoices = 0

let audioCtx: AudioContext | null = null
let audioCtxUnavailable = false

const getAudioContext = (): AudioContext | null => {
  if (audioCtx || audioCtxUnavailable) return audioCtx
  if (typeof window === "undefined") return null
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) {
    audioCtxUnavailable = true
    return null
  }
  try {
    audioCtx = new Ctor()
  } catch {
    audioCtxUnavailable = true
    return null
  }
  return audioCtx
}

/**
 * Browsers start the audio context suspended until a user gesture. Call this
 * from any early interaction (the title screen's Start button already is one).
 */
export const unlockAudio = (): void => {
  const ctx = getAudioContext()
  if (ctx && ctx.state === "suspended") {
    ctx.resume().catch(() => {
      // Still locked - the next gesture will try again
    })
  }
}

if (typeof window !== "undefined") {
  const onGesture = () => unlockAudio()
  window.addEventListener("pointerdown", onGesture, { passive: true })
  window.addEventListener("keydown", onGesture, { passive: true })
  window.addEventListener("touchstart", onGesture, { passive: true })
}

/** Fetch + decode a clip in the background so later plays take the cheap path. */
const ensureBuffer = (src: string): AudioBuffer | null => {
  const cached = bufferCache.get(src)
  if (cached) return cached
  if (decodingSources.has(src) || undecodableSources.has(src)) return null

  const ctx = getAudioContext()
  if (!ctx) return null

  decodingSources.add(src)
  fetch(src)
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.arrayBuffer()
    })
    .then((data) => ctx.decodeAudioData(data))
    .then((buffer) => {
      bufferCache.set(src, buffer)
    })
    .catch(() => {
      // Keep using the HTMLAudioElement fallback for this clip
      undecodableSources.add(src)
    })
    .finally(() => {
      decodingSources.delete(src)
    })

  return null
}

// Initialize audio pool for a sound
const initAudioPool = (src: string): HTMLAudioElement[] => {
  const pool: HTMLAudioElement[] = []
  for (let i = 0; i < POOL_SIZE; i++) {
    const audio = new Audio(src)
    audio.preload = "auto"
    pool.push(audio)
  }
  return pool
}

// Get an available audio element from the pool
const getAvailableAudio = (src: string): HTMLAudioElement | null => {
  let pool = audioCache.get(src)
  if (!pool) {
    pool = initAudioPool(src)
    audioCache.set(src, pool)
  }

  // Find an audio element that's not playing or create new if all are busy
  for (const audio of pool) {
    if (audio.paused || audio.ended) {
      return audio
    }
  }

  // All are busy, reuse the first one (will restart it)
  return pool[0]
}

// Default volume for sound effects
const DEFAULT_VOLUME = 0.4

/**
 * Play one clip. Shared by every playX helper below.
 */
const playSrc = (src: string, rawVolume: number): void => {
  // Nothing to hear while the tab is backgrounded, and iOS charges for it anyway
  if (typeof document !== "undefined" && document.hidden) return

  // User SFX volume setting scales every effect; at 0 skip entirely
  const volume = rawVolume * getSfxVolume()
  if (volume <= 0) return

  const now = typeof performance !== "undefined" ? performance.now() : Date.now()
  const last = lastPlayedAt.get(src)
  if (last !== undefined && now - last < MIN_REPLAY_INTERVAL_MS) return
  lastPlayedAt.set(src, now)

  const ctx = getAudioContext()
  const buffer = ensureBuffer(src)

  if (ctx && buffer) {
    if (activeVoices >= MAX_CONCURRENT_VOICES) return
    if (ctx.state === "suspended") {
      // Not unlocked yet - let the element path try, it may be allowed
      playSrcViaElement(src, volume)
      return
    }
    const source = ctx.createBufferSource()
    source.buffer = buffer
    const gain = ctx.createGain()
    gain.gain.value = volume
    source.connect(gain)
    gain.connect(ctx.destination)
    activeVoices++
    source.onended = () => {
      activeVoices--
      source.disconnect()
      gain.disconnect()
    }
    source.start()
    return
  }

  playSrcViaElement(src, volume)
}

const playSrcViaElement = (src: string, volume: number): void => {
  const audio = getAvailableAudio(src)
  if (!audio) return
  audio.volume = volume
  audio.currentTime = 0
  audio.play().catch(() => {
    // Ignore autoplay errors - user hasn't interacted yet
  })
}

// Play a random sound from a list
export const playSound = (sounds: readonly string[], volume: number = DEFAULT_VOLUME): void => {
  if (sounds.length === 0) return

  const src = sounds[Math.floor(Math.random() * sounds.length)]
  playSrc(src, volume)
}

// Play tower fire sound
export const playTowerFireSound = (towerType: string, volume: number = DEFAULT_VOLUME): void => {
  const sounds = TOWER_SOUNDS[towerType as TowerType]?.fire
  if (sounds) {
    playSound(sounds, volume)
  }
}

// Play tower hit sound
export const playTowerHitSound = (towerType: string, volume: number = DEFAULT_VOLUME): void => {
  const sounds = TOWER_SOUNDS[towerType as TowerType]?.hit
  if (sounds) {
    playSound(sounds, volume)
  }
}

// Play a single sound file
export const playSingleSound = (src: string, volume: number = DEFAULT_VOLUME): void => {
  playSrc(src, volume)
}

// Event sounds
export const playLevelCompleteSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(EVENT_SOUNDS.levelComplete, volume)
}

export const playDefeatSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(EVENT_SOUNDS.defeat, volume)
}

export const playNewWaveSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(EVENT_SOUNDS.newWave, volume)
}

export const playMenuHoverSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(EVENT_SOUNDS.menuHover, volume)
}

export const playSingleCoinSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(EVENT_SOUNDS.singleCoin, volume)
}

export const playAbductionSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(EVENT_SOUNDS.abduction, volume)
}

export const playSpaceshipEngineSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(EVENT_SOUNDS.spaceshipEngine, volume)
}

export const playShopOpenSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(EVENT_SOUNDS.shopOpen, volume)
}

export const playScoreBlipSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(EVENT_SOUNDS.scoreBlip, volume)
}

export const playLevelUpSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(EVENT_SOUNDS.levelUp, volume)
}

export const playCloneAwakenSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(EVENT_SOUNDS.cloneAwaken, volume)
}

export const playUpgradeTreeUpgradeSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(EVENT_SOUNDS.upgradeTreeUpgrade, volume)
}

export const playSkillLevelUpSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(EVENT_SOUNDS.skillLevelUp, volume)
}

export const playSellSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(EVENT_SOUNDS.sell, volume)
}

export const playBuySound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(EVENT_SOUNDS.buy, volume)
}

export const playEnemyFinishLineSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(EVENT_SOUNDS.enemyFinishLine, volume)
}

export const playArmorEquipSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(EVENT_SOUNDS.armorEquip, volume)
}

export const playArmorDropSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(EVENT_SOUNDS.armorDrop, volume)
}

export const playBillboardSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(EVENT_SOUNDS.billboard, volume)
}

export const playItemDropSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(EVENT_SOUNDS.itemDrop, volume)
}

// Hero ability sounds
export const playSiegesmithForgeTurretSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(HERO_SOUNDS.siegesmith.forgeTurret, volume)
}

export const playSiegesmithTurretAttackSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(HERO_SOUNDS.siegesmith.turretAttack, volume)
}

export const playSiegesmithTremorSlamSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(HERO_SOUNDS.siegesmith.tremorSlam, volume)
}

export const playPaladinVanguardRushHitSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(HERO_SOUNDS.paladin.vanguardRushHit, volume)
}

export const playPaladinVanguardRushTravelSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(HERO_SOUNDS.paladin.vanguardRushTravel, volume)
}

export const playPaladinSpectralBladeSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(HERO_SOUNDS.paladin.spectralBlade, volume)
}

export const playRangerEntangledRootsSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(HERO_SOUNDS.ranger.entangledRoots, volume)
}

export const playRangerWildkinCallSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(HERO_SOUNDS.ranger.wildkinCall, volume)
}

export const playRangerAttackAimSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(HERO_SOUNDS.ranger.attackAim, volume)
}

export const playRangerAttackReleaseSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(HERO_SOUNDS.ranger.attackRelease, volume)
}

// Tower action sounds
export const playTowerBuildSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(TOWER_ACTION_SOUNDS.build, volume)
}

export const playTowerSellSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(TOWER_ACTION_SOUNDS.sell, volume)
}

export const playBuildingHitSound = (volume: number = DEFAULT_VOLUME): void => {
  playSound(TOWER_ACTION_SOUNDS.buildingHit, volume)
}

// Item sounds
export const playPotionSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(ITEM_SOUNDS.potion, volume)
}

// Droppod sounds
export const playDroppodFlySound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(DROPPOD_SOUNDS.fly, volume)
}

export const playDroppodLandSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(DROPPOD_SOUNDS.land, volume)
}

export const playDroppodMenuSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(DROPPOD_SOUNDS.menu, volume)
}

export const playDroppodMenuClickSound = (volume: number = DEFAULT_VOLUME): void => {
  playSingleSound(DROPPOD_SOUNDS.menuClick, volume)
}

// Export all sound paths for preloading
export const getAllSounds = (): string[] => {
  const sounds: string[] = []
  // Tower sounds
  for (const tower of Object.values(TOWER_SOUNDS)) {
    sounds.push(...tower.fire)
    sounds.push(...tower.hit)
  }
  // Tower action sounds
  sounds.push(TOWER_ACTION_SOUNDS.build, TOWER_ACTION_SOUNDS.sell, ...TOWER_ACTION_SOUNDS.buildingHit)
  // Event sounds
  sounds.push(...Object.values(EVENT_SOUNDS))
  // Hero sounds
  for (const hero of Object.values(HERO_SOUNDS)) {
    sounds.push(...Object.values(hero))
  }
  // Item sounds
  sounds.push(...Object.values(ITEM_SOUNDS))
  // Droppod sounds
  sounds.push(...Object.values(DROPPOD_SOUNDS))
  return sounds
}
